import React, { useState, useEffect } from "react";
import Icon from "components/AppIcon";
import api from "../../../lib/api";
import { GYM_AMENITIES } from "utils/constants";
import RazorpaySettings from "./RazorpaySettings";

export default function GymSettings() {
  const [activeTab, setActiveTab] = useState("amenities");
  const [checked, setChecked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get('/gym-settings')
      .then(res => {
        const stored = res?.data?.amenities;
        setChecked(Array.isArray(stored) ? stored : []);
      })
      .catch(() => setChecked([]))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (amenity) => {
    setChecked(prev =>
      prev.includes(amenity) ? prev.filter(a => a !== amenity) : [...prev, amenity]
    );
    setSaved(false);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.patch('/gym-settings', { amenities: checked });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('Error saving amenities:', err);
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { key: "amenities",    label: "Amenities" },
    { key: "payments",     label: "Payments" },
    { key: "integrations", label: "Integrations" },
  ];

  return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] shadow-[var(--shadow-sm)] overflow-hidden">
      <div className="p-4 border-b border-[var(--color-border)]">
        <h3 className="text-sm font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">Gym Settings</h3>
        <div className="flex gap-1 mt-3 bg-[var(--color-muted)] rounded-md p-1">
          {tabs.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex-1 py-1.5 text-xs font-medium font-[var(--font-caption)] rounded transition-colors ${
                activeTab === key
                  ? "bg-[var(--color-card)] text-[var(--color-foreground)] shadow-sm"
                  : "text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-4">
        {/* Amenities tab */}
        {activeTab === "amenities" && (
          <>
            {loading ? (
              <div className="flex items-center justify-center py-6">
                <Icon name="Loader2" size={16} color="var(--color-muted-foreground)" className="animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {GYM_AMENITIES.map(amenity => (
                  <label key={amenity} className="flex items-center gap-2 p-2.5 rounded-md border border-[var(--color-border)] cursor-pointer hover:bg-[var(--color-muted)]/30 transition-colors">
                    <input
                      type="checkbox"
                      checked={checked.includes(amenity)}
                      onChange={() => toggle(amenity)}
                      className="rounded"
                    />
                    <span className="text-xs font-medium font-[var(--font-caption)] text-[var(--color-foreground)]">{amenity}</span>
                  </label>
                ))}
              </div>
            )}
            {!loading && (
              <div className="flex justify-end mt-3">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors disabled:opacity-60"
                >
                  {saving ? (
                    <Icon name="Loader2" size={12} color="white" className="animate-spin" />
                  ) : saved ? (
                    <Icon name="Check" size={12} color="white" />
                  ) : null}
                  {saving ? "Saving..." : saved ? "Saved" : "Save Amenities"}
                </button>
              </div>
            )}
          </>
        )}

        {/* Payments tab — Razorpay configuration */}
        {activeTab === "payments" && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-md bg-blue-50 dark:bg-blue-950/30 flex items-center justify-center">
                <Icon name="CreditCard" size={14} color="#3b82f6" />
              </div>
              <div>
                <p className="text-xs font-semibold font-[var(--font-caption)] text-[var(--color-foreground)]">Razorpay</p>
                <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">Accept online payments from members</p>
              </div>
            </div>
            <RazorpaySettings />
          </div>
        )}

        {/* Integrations tab — other services (coming soon) */}
        {activeTab === "integrations" && (
          <div className="space-y-2">
            {[
              { name: "WhatsApp Notifications", desc: "Send reminders via WhatsApp", icon: "MessageCircle" },
              { name: "Google Calendar",         desc: "Sync trainer schedules",       icon: "Calendar" },
              { name: "Zoom",                    desc: "Virtual training sessions",    icon: "Video" },
            ].map(item => (
              <div key={item.name} className="flex items-center justify-between p-3 rounded-md border border-[var(--color-border)]">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-md bg-[var(--color-muted)] flex items-center justify-center">
                    <Icon name={item.icon} size={14} color="var(--color-muted-foreground)" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold font-[var(--font-caption)] text-[var(--color-foreground)]">{item.name}</p>
                    <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{item.desc}</p>
                  </div>
                </div>
                <span className="px-2.5 py-1 text-xs font-medium rounded font-[var(--font-caption)] bg-[var(--color-muted)] text-[var(--color-muted-foreground)]">
                  Coming soon
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
